import { Stack } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { Evaluation_Milestone, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ExpandableEvaluationMilestoneCard } from 'src/screens/shared/evaluation-milestone/evaluation-milestone-card/ExpandableEvaluationMilestoneCard'
import { queryEvaluationMilestonesByDossierId } from 'src/screens/shared/evaluation-milestone/evaluationMilestonesQueries'
import { AddButton } from 'src/shared/button/Buttons'
import { PROJECT_TYPE } from 'src/shared/constants/constants'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

interface Props {
  dossierId: number
  canEdit: boolean
  projectType: PROJECT_TYPE
  onAdd: () => void
  onEdit: (id: number) => void
}

export const EvaluationMilestonesSection = ({ dossierId, canEdit, projectType, onAdd, onEdit }: Props) => {
  const { getMessage } = useMessageSource()

  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [evaluationMilestones, setEvaluationMilestones] = useState<Evaluation_Milestone[]>()

  const fetchData = useCallback(async () => {
    const { data } = await urqlClient
      .query<{ evaluation_milestone: Query_Root['evaluation_milestone'] }, { dossierId: number }>(
        queryEvaluationMilestonesByDossierId,
        { dossierId: dossierId },
      )
      .toPromise()

    if (data) {
      setEvaluationMilestones(data.evaluation_milestone)
    } else {
      notificationService.operationFailed()
    }
  }, [dossierId, notificationService, urqlClient])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleAddNewEntry = () => {
    onAdd()
  }

  const handleEdit = (evaluationMilestoneId: number) => {
    onEdit(evaluationMilestoneId)
  }

  return (
    <Section
      title={getMessage('label.title.evaluation.milestones')}
      actionButton={
        canEdit && <AddButton messageKey={getMessage('button.add.evaluation.milestone')} onClick={handleAddNewEntry} />
      }
      helpAndInstructions={<HelpAndInstructions labelKey={`label.help.project.evaluation.milestones.${projectType}`} />}
      optional
    >
      {evaluationMilestones?.length && (
        <Stack spacing={1}>
          {evaluationMilestones?.map((it) => {
            return (
              <ExpandableEvaluationMilestoneCard
                key={it.id}
                id={String(it.id)}
                projectType={projectType}
                evaluationMilestone={it}
                canEdit={canEdit}
                onEdit={() => handleEdit(it.id)}
                refetch={fetchData}
              />
            )
          })}
        </Stack>
      )}
    </Section>
  )
}
